import React from 'react';
import { Link } from 'react-router-dom';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function fmtDate(ts, locale) {
  if (!ts) return 'N/A';
  try {
    return new Date(Number(ts)).toLocaleString(getLocaleDateFormatTag(locale));
  } catch {
    return 'N/A';
  }
}

export default function ClassStudentDetailPage({ session, classId, studentId }) {
  const { t, locale } = useAppTranslation();
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    if (!token) return undefined;
    setLoading(true);
    api
      .getClassStudentDetail(classId, studentId, token)
      .then((x) => {
        if (!mounted) return;
        setData(x);
      })
      .catch((e) => setError(formatApiErrorMessage(e?.message, t) || t('errors.loadClassStudent')))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [api, classId, studentId, token, t]);

  const student = data?.student;
  const histories = Array.isArray(data?.histories) ? data.histories : [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-headline text-3xl font-extrabold">{t('class.studentDetailTitle')}</h1>
        <Link
          to={`/classes/${encodeURIComponent(classId)}`}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#004ac6] hover:text-[#004ac6]"
        >
          <span className="material-symbols-outlined !text-base">arrow_back</span>
          {t('class.backToClass')}
        </Link>
      </div>

      {loading ? <p className="text-sm text-slate-600">{t('class.loading')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}

      {student ? (
        <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-extrabold text-slate-900">{student.name}</h2>
          <div className="mt-2 grid gap-2 text-sm text-slate-700 md:grid-cols-2">
            <p>{t('childLearning.emailId')} {student.email || student.id}</p>
            <p>{t('childLearning.age')} {student.age || 'N/A'}</p>
            <p>{t('childLearning.school')} {student.school || 'N/A'}</p>
            <p>{t('childLearning.phone')} {student.phone || 'N/A'}</p>
            <p className="md:col-span-2">{t('childLearning.address')} {student.address || 'N/A'}</p>
            <p className="md:col-span-2">{t('childLearning.parentInfo')} {student.parent_info || 'N/A'}</p>
          </div>
        </section>
      ) : null}

      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-bold">{t('class.studentAssignmentHistory')}</h3>
        <div className="mt-3 space-y-2">
          {histories.map((h, idx) => (
            <article key={`${h.quiz_id}-${h.result_id || idx}`} className="rounded-xl border border-slate-100 p-3">
              <p className="font-semibold text-slate-900">{h.quiz_title}</p>
              <p className="text-xs text-slate-500">
                {t('class.assignmentMeta', {
                  assignedAt: fmtDate(h.assigned_at, locale),
                  dueAt: fmtDate(h.due_at, locale),
                })}
              </p>
              {h.result_id ? (
                <p className="mt-1 text-sm text-slate-700">
                  {t('class.studentScoreLine', {
                    score: h.score,
                    correct: h.correct_answers,
                    total: h.total_questions,
                    submittedAt: fmtDate(h.timestamp, locale),
                  })}
                </p>
              ) : (
                <p className="mt-1 text-sm font-semibold text-amber-700">{t('history.notSubmitted')}</p>
              )}
            </article>
          ))}
          {histories.length === 0 ? <p className="text-sm text-slate-500">{t('class.noHistory')}</p> : null}
        </div>
      </section>
    </div>
  );
}
